import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinkIcon from '@mui/icons-material/Link';
import ShareIcon from '@mui/icons-material/Share';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SyncIcon from '@mui/icons-material/Sync';
import { DataGrid, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import { IAdminWorksheet, useAdminUserWorksheetsQuery } from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import ShareWorksheetsDialog from './ShareWorksheetsDialog';

interface Props {
	userId: number;
	/** Fill the parent (a flex column) instead of using a fixed viewport-based height. */
	fill?: boolean;
}

function ChipList({ items, color }: { items: string[]; color: 'default' | 'primary' | 'secondary' }) {
	if (!items || items.length === 0) {
		return null;
	}
	return (
		<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
			{items.map((item) => (
				<Chip key={item} label={item} size="small" color={color} variant="outlined" />
			))}
		</Stack>
	);
}

export default function UserWorksheetsTable({ userId, fill }: Props) {
	const { data: worksheets, isLoading } = useAdminUserWorksheetsQuery(userId);

	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);

	const selectedWorksheets: IAdminWorksheet[] = (worksheets ?? []).filter((w) => selectionModel.includes(w.id));

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			minWidth: 140,
		},
		{
			field: 'period',
			headerName: 'Period',
			width: 110,
		},
		{
			field: 'period_compares',
			headerName: 'Compares',
			width: 120,
			valueFormatter: ({ value }) =>
				Array.isArray(value) && value.length > 0 ? value.join(', ') : '',
		},
		{
			field: 'group_by',
			headerName: 'Group By',
			width: 110,
		},
		{
			field: 'color_format',
			headerName: 'Color Format',
			width: 120,
		},
		{
			field: 'trend_summary_period',
			headerName: 'Trend Period',
			width: 110,
		},
		{
			field: 'created_at',
			headerName: 'Created',
			width: 110,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'last_viewed',
			headerName: 'Last Viewed',
			width: 120,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'inputs',
			headerName: 'Fields',
			flex: 1.5,
			minWidth: 180,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams) => (
				<ChipList items={params.value as string[]} color="primary" />
			),
		},
		{
			field: 'universe',
			headerName: 'Universe',
			flex: 1.5,
			minWidth: 180,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams) => (
				<ChipList items={params.value as string[]} color="secondary" />
			),
		},
		{
			field: 'shared_from_email',
			headerName: 'Shared From',
			width: 220,
			renderCell: (params: GridRenderCellParams) => {
				if (!params.value) return null;
				return (
					<Stack direction="row" spacing={0.5} alignItems="center">
						{params.row.is_sync_share && (
							<Tooltip title="Auto-synced"><SyncIcon fontSize="small" color="primary" /></Tooltip>
						)}
						{params.row.admin_shared_by_email && (
							<Tooltip title={`Shared by admin: ${params.row.admin_shared_by_email}`}>
								<AdminPanelSettingsIcon fontSize="small" color="warning" />
							</Tooltip>
						)}
						<Typography variant="body2">{params.value}</Typography>
					</Stack>
				);
			},
		},
		{
			field: 'get_link',
			headerName: 'Get Link',
			width: 90,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title="Copy link to clipboard">
					<IconButton
						size="small"
						onClick={() =>
							navigator.clipboard.writeText(
								`https://app.arbinsights.com/worksheet-builder/${params.row.id}`
							)
						}
					>
						<LinkIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box sx={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { height: 'calc(100vh - 310px)', minHeight: 300 }}>
			{selectionModel.length > 0 && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
					<Button
						variant="contained"
						size="small"
						startIcon={<ShareIcon />}
						onClick={() => setShareDialogOpen(true)}
					>
						Share {selectionModel.length} Worksheet{selectionModel.length !== 1 ? 's' : ''}
					</Button>
				</Box>
			)}
			<DataGrid
				rows={worksheets ?? []}
				columns={columns}
				loading={isLoading}
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectionModel}
				onSelectionModelChange={setSelectionModel}
				components={{ Toolbar: AdminQuickFilterToolbar }}
				density="compact"
				getRowHeight={() => 'auto'}
				sx={[{
					'& .MuiDataGrid-cell': {
						py: 0.75,
						alignItems: 'flex-start',
					},
				}, fill ? { flex: 1, minHeight: 0 } : null]}
			/>
			<ShareWorksheetsDialog
				open={shareDialogOpen}
				onClose={() => {
					setShareDialogOpen(false);
					setSelectionModel([]);
				}}
				worksheets={selectedWorksheets}
				sourceUserId={userId}
			/>
		</Box>
	);
}
